<template>
  <Dialog
    v-model:visible="visible"
    modal
    dismissableMask
    :closable="false"
    :draggable="false"
    :pt="{ root: { style: 'background: transparent; border: 0; box-shadow: none;' } }"
  >
    <template #container="{ closeCallback }">
      <div v-if="current" class="relative">
        <img
          :src="IMAGES[current.kind]"
          :alt="current.kind === 'BIRTHDAY' ? t('celebration.birthdayAlt') : t('celebration.anniversaryAlt')"
          class="block rounded-2xl shadow-2xl bg-white"
          style="max-width: min(92vw, 880px); max-height: 85vh; object-fit: contain"
        />
        <button
          type="button"
          class="absolute top-2 right-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-gray-800 shadow-md transition hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          :aria-label="t('common.close')"
          @click="closeCallback"
        >
          <i class="pi pi-times" aria-hidden="true" />
        </button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Dialog from 'primevue/dialog'
import { useAuthStore } from '@/stores/auth.store'
import { dashboardService } from '@/services/dashboard.service'
import type { CelebrationKind, PendingCelebration } from '@/services/dashboard.service'
import { unwrapApiResponse } from '@/utils/api-response'

/**
 * Giriş yapan kişinin (ofis personeli veya çalışan) o gün doğum günü / iş yıl dönümü ise
 * ilk açılışta kutlama görselini gösterir. Kapatılınca sunucuya bildirilir; aynı gün tekrar çıkmaz.
 */
const IMAGES: Record<CelebrationKind, string> = {
  BIRTHDAY: '/celebrations/birthday.webp',
  ANNIVERSARY: '/celebrations/anniversary.webp',
}

const { t } = useI18n()
const authStore = useAuthStore()

const queue = ref<PendingCelebration[]>([])
const current = computed(() => queue.value[0] ?? null)

const visible = computed({
  get: () => current.value !== null,
  set: (open: boolean) => {
    if (!open) dismissCurrent()
  },
})

function dismissCurrent() {
  const done = queue.value[0]
  if (!done) return
  queue.value = queue.value.slice(1)
  dashboardService.markCelebrationSeen(done.kind).catch(() => {
    // Kayıt başarısız olursa görsel bir sonraki açılışta yeniden gösterilir
  })
}

let loaded = false

async function load() {
  if (loaded) return
  loaded = true
  try {
    const response = await dashboardService.getCelebration()
    const data = unwrapApiResponse<{ items: PendingCelebration[] }>(response)
    queue.value = data?.items ?? []
  } catch {
    // Kozmetik bir özellik: alınamazsa sessizce atla
  }
}

watch(
  () => authStore.isAuthenticated,
  (authenticated) => {
    if (authenticated) void load()
  },
  { immediate: true },
)
</script>
